import { useState, useEffect } from 'react';
import { TrendingUp, Shield, Zap, Users, Star, ArrowRight, CheckCircle, Calculator, Phone, Award, Clock, Heart } from 'lucide-react';
import LoanCard from "../components/LoanCard";

export default function Home({ setLoanData }) {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Navigation function for hash routing
  const navigateTo = (path) => {
    window.location.hash = path;
  };

  const features = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Quick Approval",
      description: "Get loan approval within 24 hours with minimal documentation"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Secure Process",
      description: "Bank-grade security to protect your personal information"
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Best Rates",
      description: "Competitive interest rates starting from 7.0% per annum"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Expert Support",
      description: "Dedicated relationship managers to guide you through"
    }
  ];

  const testimonials = [
    {
      name: "Rajesh Kumar",
      role: "Business Owner",
      text: "DhanPlus helped me get my business loan approved within 48 hours. The process was seamless!",
      rating: 5,
      image: "RK"
    },
    {
      name: "Priya Sharma",
      role: "Homeowner",
      text: "Got my dream home with their excellent home loan rates. Highly recommended!",
      rating: 5,
      image: "PS"
    },
    {
      name: "Amit Patel",
      role: "Software Engineer",
      text: "The EMI calculator helped me plan my finances perfectly. Great service!",
      rating: 5,
      image: "AP"
    }
  ];

  const stats = [
    { value: "50K+", label: "Happy Customers", icon: <Users className="w-6 h-6" /> },
    { value: "₹500Cr+", label: "Loans Disbursed", icon: <TrendingUp className="w-6 h-6" /> },
    { value: "7.0%", label: "Starting Rate", icon: <Award className="w-6 h-6" /> },
    { value: "24hrs", label: "Quick Approval", icon: <Clock className="w-6 h-6" /> }
  ];

  const benefits = [
    "No hidden charges or processing fees",
    "Flexible repayment options up to 30 years",
    "Balance transfer facility available",
    "Pre-approved loan offers for existing customers",
    "Digital documentation process",
    "Free credit score check"
  ];

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white min-h-screen">
      {/* Hero Section - Fully Responsive for Mobile */}
      <div className={`relative text-white overflow-hidden transition-all duration-1000 h-[60vh] sm:h-[65vh] md:h-[70vh] lg:h-[75vh] ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
        {/* Responsive Video Background Container */}
        <div className="absolute inset-0 w-full h-full">
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="video-background"
          >
            {/* Use absolute path from public folder */}
            <source src="/assets/hero-video.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>

        {/* Content - Empty Space Maintained - Responsive Padding */}
        <div className="relative z-10 w-full h-full flex items-center justify-center px-4 sm:px-6 md:px-10">
          <div className="text-center w-full max-w-7xl mx-auto">
            {/* Text removed - space maintained */}
          </div>
        </div>
      </div>

      {/* Stats Section - Mobile Responsive */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 -mt-8 sm:-mt-10 md:-mt-12 relative z-10">
        <div className="bg-white rounded-xl md:rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="text-center group hover:scale-105 transition-transform duration-300"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-full text-blue-800 mb-2 sm:mb-3 group-hover:bg-blue-800 group-hover:text-white transition-colors duration-300">
                {stat.icon}
              </div>
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-1">{stat.value}</div>
              <div className="text-xs sm:text-sm text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-12 sm:py-14 md:py-16">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 text-center mb-3 sm:mb-4">
          Compare & Apply Loans with <span className="text-blue-800">DhanPlus</span>
        </h2>
        <p className="text-center text-sm sm:text-base text-gray-600 mb-8 sm:mb-10 md:mb-12 max-w-2xl mx-auto px-4">
          Choose from our wide range of loan products designed to meet your specific financial goals
        </p>

        {/* Loan Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-5 md:gap-6 mb-12 sm:mb-14 md:mb-16">
          <LoanCard title="Home Loan" rate="8.5" />
          <LoanCard title="Car Loan" rate="9" />
          <LoanCard title="Business Loan" rate="10.5" />
        </div>

        {/* Features Section */}
        <div className="mb-12 sm:mb-14 md:mb-16">
          <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8 sm:mb-10 md:mb-12">
            Why Choose DhanPlus?
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 md:gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-5 sm:p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 group hover:-translate-y-2"
              >
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center text-white mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">{feature.title}</h4>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* EMI Calculator CTA */}
        <div className="mb-12 sm:mb-14 md:mb-16 bg-gradient-to-r from-blue-800 to-blue-900 rounded-xl md:rounded-2xl p-6 sm:p-8 md:p-12 text-white text-center">
          <Calculator className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 mx-auto mb-4 sm:mb-5 md:mb-6 text-yellow-400" />
          <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4">
            Calculate Your EMI
          </h3>
          <p className="text-sm sm:text-base text-blue-100 mb-6 sm:mb-7 md:mb-8 max-w-2xl mx-auto px-4">
            Use our advanced EMI calculator to plan your loan repayment and make informed financial decisions.
          </p>
          <button
            onClick={() => navigateTo('calculator')}
            className="bg-yellow-400 text-blue-900 px-6 sm:px-7 md:px-8 py-3 sm:py-3.5 md:py-4 rounded-lg font-semibold hover:bg-yellow-300 transform hover:scale-105 transition-all duration-200 shadow-lg inline-flex items-center space-x-2 text-sm sm:text-base"
          >
            <Calculator className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>Calculate EMI</span>
          </button>
        </div>

        {/* Benefits Section */}
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl md:rounded-2xl p-6 sm:p-8 md:p-12 mb-12 sm:mb-14 md:mb-16">
          <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-6 sm:mb-7 md:mb-8 text-center">
            Exclusive Benefits
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="flex items-start space-x-3 bg-white/60 backdrop-blur-sm p-3 sm:p-4 rounded-lg hover:bg-white transition-all duration-300"
              >
                <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm sm:text-base text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonials */}
        <div className="mb-12 sm:mb-14 md:mb-16">
          <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8 sm:mb-10 md:mb-12">
            What Our Customers Say
          </h3>
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl md:rounded-2xl shadow-xl p-6 sm:p-8 md:p-12 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-blue-100 rounded-full -mr-12 sm:-mr-16 -mt-12 sm:-mt-16 opacity-50"></div>
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className={`transition-all duration-500 ${activeTestimonial === index
                    ? 'opacity-100 block'
                    : 'opacity-0 hidden'
                    }`}
                >
                  <div className="flex items-center mb-4 sm:mb-6">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white font-bold text-lg sm:text-xl mr-3 sm:mr-4">
                      {testimonial.image}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-base sm:text-lg">{testimonial.name}</h4>
                      <p className="text-gray-600 text-xs sm:text-sm">{testimonial.role}</p>
                    </div>
                  </div>
                  <div className="flex mb-3 sm:mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-700 text-sm sm:text-base md:text-lg italic">"{testimonial.text}"</p>
                </div>
              ))}
              <div className="flex justify-center mt-4 sm:mt-6 space-x-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveTestimonial(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${activeTestimonial === index
                      ? 'bg-blue-800 w-6 sm:w-8'
                      : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-800 to-blue-900 rounded-xl md:rounded-2xl p-6 sm:p-8 md:p-12 text-white text-center">
          <Heart className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 mx-auto mb-4 sm:mb-5 md:mb-6 text-yellow-400" />
          <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4">
            Ready to Make Your Dreams Come True?
          </h3>
          <p className="text-sm sm:text-base text-blue-100 mb-6 sm:mb-7 md:mb-8 max-w-2xl mx-auto px-4">
            Join thousands of satisfied customers who trusted DhanPlus for their financial needs.
            Start your loan application today!
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <button
              onClick={() => navigateTo('apply')}
              className="bg-yellow-400 text-blue-900 px-6 sm:px-7 md:px-8 py-3 sm:py-3.5 md:py-4 rounded-lg font-semibold hover:bg-yellow-300 transform hover:scale-105 transition-all duration-200 shadow-lg text-sm sm:text-base"
            >
              Start Application
            </button>
            <button
              onClick={() => navigateTo('contact')}
              className="bg-white/10 backdrop-blur-sm border-2 border-white px-6 sm:px-7 md:px-8 py-3 sm:py-3.5 md:py-4 rounded-lg font-semibold hover:bg-white/20 transition-all duration-200 flex items-center justify-center space-x-2 text-sm sm:text-base"
            >
              <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Contact Us</span>
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        /* Responsive Video Background Styles */
        .video-background {
          position: absolute;
          top: 50%;
          left: 50%;
          min-width: 100%;
          min-height: 100%;
          width: auto;
          height: auto;
          transform: translate(-50%, -50%);
          object-fit: cover;
          z-index: 0;
        }

        /* Mobile Optimization */
        @media (max-width: 768px) {
          .video-background {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
        }

        /* Portrait Mode Optimization */
        @media (orientation: portrait) {
          .video-background {
            width: auto;
            height: 100%;
            min-width: 100%;
          }
        }

        /* Landscape Mode Optimization */
        @media (orientation: landscape) {
          .video-background {
            width: 100%;
            height: auto;
            min-height: 100%;
          }
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out forwards;
        }

        .animation-delay-200 {
          animation-delay: 200ms;
        }

        .animation-delay-400 {
          animation-delay: 400ms;
        }
      `}</style>
    </div>
  );
}
